import { useCallback, useEffect, useRef, useState } from "react";

import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";

/**
 * One fetch, one abort, one honest error.
 *
 * Two rules this hook exists to keep:
 *
 *   Personal data is never cached. `cache` is opt-in and only the aggregate
 *   endpoints pass it; the cohort, payments and activity calls carry names and
 *   emails, and sessionStorage on a shared admin machine is not where those
 *   belong.
 *
 *   A failed request says so. The tab this replaced swallowed its error and
 *   rendered an empty state, which reads as "no customers" rather than "this
 *   did not load" — the one mistake a payments screen cannot make.
 */
export default function useLicensingQuery(fetchFn, namespace, params, { cache = false, skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const key = JSON.stringify(params ?? {});
  // Only the newest request may write state: filters change faster than a
  // slow response comes back, and the stale one must not win.
  const requestRef = useRef(0);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return undefined;
    }
    const id = ++requestRef.current;
    const controller = new AbortController();
    const cacheKey = cache ? mcapCacheKey(`licensing:${namespace}`, params ?? {}) : null;

    if (cacheKey) {
      const hit = getCached(cacheKey);
      if (hit) {
        setData(hit);
        setLoading(false);
        setError(null);
        return undefined;
      }
    }

    setLoading(true);
    setError(null);
    fetchFn(params ?? {}, controller.signal)
      .then((res) => {
        if (id !== requestRef.current) return;
        setData(res ?? null);
        if (cacheKey) setCached(cacheKey, res);
      })
      .catch((err) => {
        // Aborting is this component moving on, not a failure.
        if (err?.name === "AbortError" || err?.name === "CanceledError") return;
        if (id !== requestRef.current) return;
        setError(err || new Error("request failed"));
        setData(null);
      })
      .finally(() => {
        if (id === requestRef.current) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, namespace, cache, skip, reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  return { data, loading, error, reload };
}
